import * as React from "react";
import { useQuery } from "convex/react";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convexApi";
import { formatCurrency, formatStatusLabel } from "@/lib/format";
import {
  exportToCSV,
  formatDateForExport,
  formatCurrencyForExport,
  type ExportColumn,
} from "@/lib/export";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { getContractStatusBadgeClass } from "@/lib/status-tag-colors";
import { matchesScopedFilter } from "./adminTabFilterUtils";

interface ContractsTabProps {
  authUserId?: string;
}

interface ContractRow {
  id: string;
  contractNumber: string;
  customer: string;
  company: string;
  isEnterprise: boolean;
  status: string;
  itemCount: number;
  totalBags: number;
  fulfilled: number;
  totalAmount: number;
  startDate: string;
  endDate: string;
  items: any[];
}

function sumContractItems(items: any[], field: string): number {
  if (!items || !Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + (item[field] || 0), 0);
}

function useColumns() {
  return React.useMemo<ColumnDef<ContractRow>[]>(
    () => [
      {
        id: "contractNumber",
        accessorKey: "contractNumber",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Contract #" />
        ),
        cell: ({ row }) => (
          <span className="text-xs font-mono font-medium">
            {row.original.contractNumber}
          </span>
        ),
        meta: {
          label: "Contract #",
          placeholder: "Search contract...",
          variant: "text" as const,
        },
        enableColumnFilter: true,
      },
      {
        id: "customer",
        accessorKey: "customer",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Customer" />
        ),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-xs font-medium">
              {row.original.company || row.original.customer}
            </span>
            {row.original.company && row.original.company !== row.original.customer && (
              <span className="text-xs text-muted-foreground">
                {row.original.customer}
              </span>
            )}
          </div>
        ),
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          const search = String(filterValue).toLowerCase();
          const customer = (row.original.customer ?? "").toLowerCase();
          const company = (row.original.company ?? "").toLowerCase();
          return customer.includes(search) || company.includes(search);
        },
        meta: {
          label: "Customer",
          placeholder: "Search customer...",
          variant: "text" as const,
        },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorKey: "isEnterprise",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Enterprise" />
        ),
        cell: ({ row }) => (
          <Badge variant="outline" className={getEnterpriseBadgeClass(row.original.isEnterprise)}>
            {getEnterpriseBadgeLabel(row.original.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Enterprise",
          variant: "select" as const,
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues) =>
          filterValues.some((value: string) => value === String(Boolean(row.original.isEnterprise))),
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Status" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getContractStatusBadgeClass(row.original.status)}
          >
            {formatStatusLabel(row.original.status)}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "text" as const,
          placeholder: "Filter status...",
        },
        enableColumnFilter: true,
      },
      {
        id: "itemCount",
        accessorKey: "itemCount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Items" />
        ),
        cell: ({ row }) => (
          <span className="text-xs tabular-nums">
            {row.original.itemCount}
          </span>
        ),
        meta: { label: "Items", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "totalBags",
        accessorKey: "totalBags",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Bags" />
        ),
        cell: ({ row }) => (
          <span className="text-xs tabular-nums">
            {row.original.totalBags.toLocaleString()}
          </span>
        ),
        meta: { label: "Bags", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "fulfilled",
        accessorKey: "fulfilled",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Fulfilled" />
        ),
        cell: ({ row }) => (
          <span className="text-xs tabular-nums">
            {row.original.fulfilled.toLocaleString()}
          </span>
        ),
        meta: { label: "Fulfilled", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "totalAmount",
        accessorKey: "totalAmount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Total $" />
        ),
        cell: ({ row }) => (
          <span className="text-xs font-medium tabular-nums">
            {formatCurrency(row.original.totalAmount)}
          </span>
        ),
        meta: { label: "Total Amount", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "startDate",
        accessorKey: "startDate",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Start" />
        ),
        cell: ({ row }) => {
          if (!row.original.startDate)
            return (
              <span className="text-xs text-muted-foreground">-</span>
            );
          return (
            <span className="text-xs">
              {new Date(row.original.startDate).toLocaleDateString()}
            </span>
          );
        },
        meta: { label: "Start Date", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "endDate",
        accessorKey: "endDate",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="End" />
        ),
        cell: ({ row }) => {
          if (!row.original.endDate)
            return (
              <span className="text-xs text-muted-foreground">-</span>
            );
          return (
            <span className="text-xs">
              {new Date(row.original.endDate).toLocaleDateString()}
            </span>
          );
        },
        meta: { label: "End Date", variant: "text" as const },
        enableColumnFilter: true,
      },
    ],
    [],
  );
}

export function ContractsTab({ authUserId }: ContractsTabProps) {
  const [searchParams] = useSearchParams();
  const columns = useColumns();
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(
    new Set(),
  );

  const contractsData = useQuery(
    api.contractWorkflow.getContractsByStatus,
    authUserId ? { authUserId } : "skip",
  );

  const isLoading = contractsData === undefined;

  const tableData = React.useMemo((): ContractRow[] => {
    if (!contractsData || !Array.isArray(contractsData)) return [];
    return (contractsData as any[]).map((contract: any) => ({
      id: contract._id || contract.contractNumber,
      contractNumber: contract.contractNumber ?? "N/A",
      customer:
        contract.user?.name ||
        contract.user?.email ||
        "Unknown",
      company: contract.user?.company || "",
      isEnterprise: Boolean(contract.user?.isEnterprise),
      status: contract.contractStatus ?? "unknown",
      itemCount: contract.items?.length ?? 0,
      totalBags: sumContractItems(contract.items, "totalBags"),
      fulfilled: sumContractItems(contract.items, "fulfilled"),
      totalAmount: contract.totalAmount ?? 0,
      startDate: contract.startDate
        ? new Date(contract.startDate).toISOString()
        : "",
      endDate: contract.endDate
        ? new Date(contract.endDate).toISOString()
        : "",
      items: contract.items || [],
    }));
  }, [contractsData]);

  const contractNumberParam = searchParams.get("contractNumber");
  const companyParam = searchParams.get("company");

  const scopedTableData = React.useMemo(
    () =>
      tableData.filter((row) => {
        if (!matchesScopedFilter([row.contractNumber], contractNumberParam)) return false;
        if (!matchesScopedFilter([row.company, row.customer], companyParam)) return false;
        return true;
      }),
    [tableData, contractNumberParam, companyParam],
  );

  const toggleExpand = React.useCallback((rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  function handleExport() {
    const exportData = scopedTableData.map((row) => ({
      contractNumber: row.contractNumber,
      customer: row.customer,
      enterprise: getEnterpriseBadgeLabel(row.isEnterprise),
      status: row.status,
      itemCount: row.itemCount,
      totalBags: row.totalBags,
      fulfilled: row.fulfilled,
      totalAmount: row.totalAmount,
      startDate: row.startDate,
      endDate: row.endDate,
    }));
    const exportColumns: ExportColumn[] = [
      { key: "contractNumber", label: "Contract #" },
      { key: "customer", label: "Customer" },
      { key: "enterprise", label: "Enterprise" },
      { key: "status", label: "Status" },
      { key: "itemCount", label: "Items" },
      { key: "totalBags", label: "Total Bags" },
      { key: "fulfilled", label: "Fulfilled" },
      {
        key: "totalAmount",
        label: "Total",
        format: formatCurrencyForExport,
      },
      { key: "startDate", label: "Start", format: formatDateForExport },
      { key: "endDate", label: "End", format: formatDateForExport },
    ];
    exportToCSV(
      exportData,
      exportColumns,
      `contracts-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xs font-semibold">Contracts</h2>
        <p className="text-xs text-muted-foreground">
          Customer contracts with delivery commitments and tiered pricing
          oversight.
        </p>
      </div>
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : scopedTableData.length > 0 ? (
        <>
          <div className="mb-2 flex items-center justify-end">
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
          <div className="overflow-hidden rounded-lg border">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-8" />
                  <TableHead>Contract #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Enterprise</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="text-right">Bags</TableHead>
                  <TableHead className="text-right">Fulfilled</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scopedTableData.map((row) => {
                  const isExpanded = expandedRows.has(row.id);
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/30"
                        onClick={() => toggleExpand(row.id)}
                      >
                        <TableCell className="w-8">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="size-6 p-0"
                          >
                            {isExpanded ? (
                              <ChevronDown className="size-3" />
                            ) : (
                              <ChevronRight className="size-3" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-mono font-medium">
                          {row.contractNumber}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">
                              {row.company || row.customer}
                            </span>
                            {row.company && row.company !== row.customer && (
                              <span className="text-xs text-muted-foreground">
                                {row.customer}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getEnterpriseBadgeClass(row.isEnterprise)}>
                            {getEnterpriseBadgeLabel(row.isEnterprise)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={getContractStatusBadgeClass(row.status)}
                          >
                            {formatStatusLabel(row.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.itemCount}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.totalBags.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.fulfilled.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatCurrency(row.totalAmount)}
                        </TableCell>
                        <TableCell>
                          {row.startDate
                            ? new Date(row.startDate).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row.endDate
                            ? new Date(row.endDate).toLocaleDateString()
                            : "-"}
                        </TableCell>
                      </TableRow>

                      {isExpanded && row.items.length > 0 && (
                        <TableRow className="bg-muted/20">
                          <TableCell colSpan={11} className="p-0">
                            <div className="px-8 py-4">
                              <p className="mb-3 text-xs font-medium text-muted-foreground">
                                Contract Items ({row.items.length})
                              </p>
                              <div className="overflow-hidden rounded border">
                                <Table className="text-xs">
                                  <TableHeader>
                                    <TableRow className="bg-muted/40">
                                      <TableHead>Product</TableHead>
                                      <TableHead className="text-right">
                                        Total Bags
                                      </TableHead>
                                      <TableHead className="text-right">
                                        Fulfilled
                                      </TableHead>
                                      <TableHead className="text-right">
                                        Remaining
                                      </TableHead>
                                      <TableHead className="text-right">
                                        Price/Bag
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {row.items.map(
                                      (item: any, idx: number) => (
                                        <TableRow
                                          key={item.productName || item.product?.name || idx}
                                          className="hover:bg-muted/20"
                                        >
                                          <TableCell className="font-medium">
                                            {item.productName ||
                                              item.product?.name ||
                                              "Unknown"}
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            {(
                                              item.totalBags || 0
                                            ).toLocaleString()}
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            {(
                                              item.fulfilled || 0
                                            ).toLocaleString()}
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            {(
                                              (item.totalBags || 0) -
                                              (item.fulfilled || 0)
                                            ).toLocaleString()}
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            {item.pricePerBag
                                              ? formatCurrency(
                                                  item.pricePerBag,
                                                )
                                              : "-"}
                                          </TableCell>
                                        </TableRow>
                                      ),
                                    )}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-muted-foreground">
          No contracts found
        </div>
      )}
    </div>
  );
}
